import * as React from "react";
import { useState } from "react";
import * as _ from "lodash";
import {
  Grid,
  InputBase,
  Divider,
  IconButton,
  Tabs,
  Tab,
  MenuList,
  MenuItem,
} from "@material-ui/core";
import { withStyles } from "@material-ui/core/styles";
import {
  Close as CloseIcon,
  ExpandMore,
  ExpandLess,
  Search,
} from "@material-ui/icons";
import { WBSStore } from "modules/WBS/WBSStore";
import { WBSItemOccurrencesStore } from "modules/WBS/WBSItemOccurrencesStore";
import { IWBSRow } from "common/interfaces";
import { ESearchTabs } from "common/enums";
import i18n from "i18n";

const styles = (theme) => ({
  root: {
    width: 422,
    position: "absolute" as any,
    top: 27,
    zIndex: 1,
  },
  topArrow: {
    width: "100%",
    zIndex: 2,
    "& span": {
      display: "block",
      width: 0,
      height: 0,
      borderLeft: "6px solid transparent",
      borderRight: "6px solid transparent",
      margin: "0 auto",
      borderBottom: "6px solid #FFFFFF",
    },
  },
  containerCard: {
    width: "100%",
    backgroundColor: "#FFFFFF",
    alignItems: "center",
    boxShadow: "0px 4px 4px rgba(0, 0, 0, 0.25)" as any,
    zIndex: 1,
  },
  tabsContainer: { minHeight: 32 },
  tabItem: {
    width: 140,
    minWidth: 140,
    minHeight: 32,
    height: 32,
    border: "1px solid transparent",
    borderBottomColor: "#BDBDBD",
    borderLeftColor: "#BDBDBD",
    zIndex: 1,
  },
  tabItemSelected: {
    color: "#424242",
    background: "#EEEEEE",
  },
  input: {
    marginLeft: 8,
    flex: 1,

    "& .icon": {
      color: "#9E9E9E",
      cursor: "pointer",
    },
  },
  iconButton: {
    padding: 10,
  },
  divider: {
    width: 1,
    height: 28,
    margin: 4,
  },
  menuOptions: {
    borderRadius: "0px 0px 4px 4px",
    borderTop: "1px solid #BDBDBD",
    "& .menu-list, & .menu-item": {
      background: "white",
      color: "#424242 !important",
    },
    "& .menu-item": {
      display: "block",
      height: "min-content",
      whiteSpace: "nowrap" as any,
      textOverflow: "ellipsis",
      overflow: "hidden",
    },
  },
});

type IProps = {
  classes: any;
  /** Propriedade para exibir/ocultar o componente. */
  open: boolean;
  /** Evento disparado ao clicar no botão para fechar o tooltip. */
  onClose: () => void;
  /** Define a posição horizontal do componente a partir do lado esquerdo. */
  leftOffset?: number;
};

/** Componente para pesquisar as linhas da EAP. */
const SearchBox = ({ classes, open, onClose, leftOffset = 135 }: IProps) => {
  const [inputRef, setInputRef] = useState<any>(undefined);
  const [tab, setTab] = useState<ESearchTabs>(0);
  const [searchTerm, setSearchTerm] = useState("");
  const [filteredRows, setFilteredRows] = useState<IWBSRow[]>([]);
  const tabItemClasses = {
    root: classes.tabItem,
    selected: classes.tabItemSelected,
  };

  const handleClose = () => {
    setTab(0);
    setSearchTerm("");
    setFilteredRows([]);
    onClose();
  };

  /** Define a aba atual a ser exibida limpando os dados necessários. */
  const handleChangeTab = (ev, value) => {
    if (tab !== value) {
      setTab(value);
      setSearchTerm("");
      setFilteredRows([]);
    }
  };

  /**
   * @description Obtém a lista de linhas filtradas a partir do termo digitado e caso
   * a aba aberta seja 'Linha', seleciona a primeira linha da lista filtrada.
   * @param event
   *
   */
  const handleSearchChange = (event) => {
    const target = event.target;

    let rows: IWBSRow[] = [];
    const isTask = WBSStore.task;

    const term = target.value.toLowerCase();
    setSearchTerm(term);

    if (tab === ESearchTabs.EAP_LINE) {
      rows = isTask
        ? WBSStore.filterTaskRowsWithTerm(term)
        : WBSStore.filterEAPRowsWithTerm(term);
    } else {
      const types = ["all", "resources", "tasks"];
      rows = WBSStore.filterAllRowsWithTerm(term, types[tab]);
    }

    if (rows.length === 0) {
      setFilteredRows([]);
      return;
    }

    // Se os valores filtrados forem diferentes
    if (!_.isEqual(rows, filteredRows)) {
      setFilteredRows(rows);
      if (tab === ESearchTabs.EAP_LINE) selectRowBy(rows[0]);
    }
  };

  /** Quando a aba for 'Linha', pula para próxima linha filtrada ou para anterior. */
  const handleMoveSearchedRow = (to: string) => {
    if (filteredRows.length <= 1) return;

    const indexOfSelectedRow = WBSStore.selectedLine
      ? WBSStore.selectedLine.index || 0
      : 0;
    const indexOnList = findRowByIndex(indexOfSelectedRow);

    let index = 0;
    if (indexOnList !== -1) {
      if (to === "next") {
        index = indexOnList < filteredRows.length - 1 ? indexOnList + 1 : 0;
      } else {
        index = indexOnList === 0 ? filteredRows.length - 1 : indexOnList - 1;
      }
    }

    selectRowBy(filteredRows[index]);
  };

  /** Quando clicar em uma opção pesquisada das abas 'Insumo' ou 'Composição', abra a
   * janela de ocorrências do item.
   */
  const handleClickOption = (contentId: string) => {
    WBSItemOccurrencesStore.fetchAndShowItemOccurences(contentId);
    handleClose();
  };

  function findRowByIndex(index: number) {
    const isTask = WBSStore.task;
    if (isTask) {
      return filteredRows.findIndex((row) => row.data.rowIndex === index);
    }
    return filteredRows.findIndex((row) => row.index === index);
  }

  /** Chama função responsável por selecionar a linha pelo índice. */
  async function selectRowBy(row: IWBSRow) {
    if (row) {
      const isTask = WBSStore.task;
      const indexToSelected = isTask ? row.data.rowIndex : row.index;
      await WBSStore.setSelectedLine(indexToSelected || 0)
        .catch((error) => console.error("Error on select searched row", error))
        .finally(() => {
          if (inputRef) inputRef.focus();
        });
    }
  }

  /** Pega a lista filtrada e devolve apenas as descrições e ids das linhas. */
  function getListOfDescriptions(): [string, string][] {
    let options: { [contentId: string]: string } = {};
    filteredRows.forEach((row) => {
      if (row.data.contentId && row.data.description) {
        options[row.data.contentId] = row.data.description;
      }
    });

    return Object.entries(options);
  }

  if (open)
    return (
      <Grid
        data-testid="grid-SearchBox"
        container
        className={classes.root}
        style={{ left: leftOffset }}
      >
        <Grid item className={classes.topArrow}>
          <span />
        </Grid>
        <Grid container item className={classes.containerCard}>
          <Tabs
            value={tab}
            variant="fullWidth"
            className={classes.tabsContainer}
            onChange={handleChangeTab}
          >
            <Tab
              label={i18n.project.searchField.line}
              data-testid="tab-SearchBoxLineTab"
              classes={tabItemClasses}
            />
            <Tab
              label={i18n.project.searchField.resources}
              data-testid="tab-SearchBoxResourcesTab"
              classes={tabItemClasses}
            />
            <Tab
              label={i18n.project.searchField.tasks}
              data-testid="tab-SearchBoxTasksTab"
              classes={tabItemClasses}
            />
          </Tabs>
          <Grid container item style={{ padding: "2px 4px" }}>
            <InputBase
              id="searchId"
              data-testid="input-SearchBox"
              inputRef={(el) => setInputRef(el)}
              value={searchTerm}
              className={classes.input}
              placeholder={i18n.project.searchField.placeholder}
              onChange={handleSearchChange}
              endAdornment={<Search className="icon" />}
            />
            <Divider className={classes.divider} />
            {tab === ESearchTabs.EAP_LINE && (
              <>
                <IconButton
                  data-testid="iconButton-SearchBoxNextLine"
                  className={classes.iconButton}
                  aria-label="Next searched"
                  onClick={() => handleMoveSearchedRow("next")}
                  disabled={!filteredRows.length}
                >
                  <ExpandMore fontSize="small" />
                </IconButton>
                <IconButton
                  data-testid="iconButton-SearchBoxPreviousLine"
                  className={classes.iconButton}
                  aria-label="Previous searched"
                  onClick={() => handleMoveSearchedRow("previous")}
                  disabled={!filteredRows.length}
                >
                  <ExpandLess fontSize="small" />
                </IconButton>
              </>
            )}
            <IconButton
              data-testid="iconButton-SearchBoxClose"
              onClick={handleClose}
              color="primary"
              className={classes.iconButton}
              aria-label="Close"
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Grid>
          {tab !== ESearchTabs.EAP_LINE && (
            <Grid container item className={classes.menuOptions}>
              <MenuList className="menu-list fullWidth no-padding">
                {getListOfDescriptions().map(([id, description]) => (
                  <MenuItem
                    className="menu-SearchBox menu-item"
                    key={id}
                    onClick={() => handleClickOption(id)}
                  >
                    {description}
                  </MenuItem>
                ))}
              </MenuList>
            </Grid>
          )}
        </Grid>
      </Grid>
    );

  return <></>;
};
export default withStyles(styles)(SearchBox);



// WEBPACK FOOTER //
// ./src/modules/project/components/layouts/SearchBox.tsx